// Admissions board — Phase 3 (2026-10-03).
//
// Rendered inside QueuesScreen under the "Admissions" segment. Lists
// open admissions (status='observing'), sorted by vitals due. Each card
// shows: patient, location, elapsed since admit, latest vitals snapshot
// chip, overdue chip, and action buttons gated by role.
//
// Nurses: Record vitals, Cancel. Doctors/admins: Record vitals,
// Discharge, Cancel. The role is pulled from the same clinicApi.myClinic
// query every other screen uses — one source of truth.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdmissionView, ClinicMemberRole } from '@pamosi/shared';
import { ApiError, admissionsApi, clinicApi } from '../lib/api';
import { VitalsQuickEntryModal } from './VitalsQuickEntryModal';
import { DischargeDialog } from './DischargeDialog';
import { ObservationChartModal } from './ObservationChartModal';
import { MarPanel } from './MarPanel';

function canDischarge(role: ClinicMemberRole | null): boolean {
    return role === 'doctor' || role === 'provider' || role === 'admin';
}
function canCancel(role: ClinicMemberRole | null): boolean {
    return role === 'doctor' || role === 'provider' || role === 'nurse' || role === 'admin';
}

export function AdmissionsBoard() {
    const { data: myClinic } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const role: ClinicMemberRole | null = myClinic?.member?.role ?? null;

    const listQ = useQuery({
        queryKey: ['admissions', { status: 'observing' }],
        queryFn: () => admissionsApi.list({ status: 'observing' }),
        refetchInterval: 30_000, // overdue chips otherwise go stale on a quiet board
    });

    const admissions = useMemo(() => {
        const list = listQ.data?.admissions ?? [];
        const now = Date.now();
        // Overdue first (next_vitals_due_at past), then by due time asc,
        // then by admitted_at desc. Pure client-side so the server stays
        // simple.
        return [...list].sort((a, b) => {
            const aOverdue = a.next_vitals_due_at ? new Date(a.next_vitals_due_at).getTime() <= now : false;
            const bOverdue = b.next_vitals_due_at ? new Date(b.next_vitals_due_at).getTime() <= now : false;
            if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
            const aDue = a.next_vitals_due_at ? new Date(a.next_vitals_due_at).getTime() : Infinity;
            const bDue = b.next_vitals_due_at ? new Date(b.next_vitals_due_at).getTime() : Infinity;
            if (aDue !== bDue) return aDue - bDue;
            return new Date(b.admitted_at).getTime() - new Date(a.admitted_at).getTime();
        });
    }, [listQ.data]);

    if (listQ.isLoading) {
        return <div className="pm-card p-8 text-center text-slate-500 text-sm">Loading admissions…</div>;
    }
    if (listQ.isError) {
        return (
            <div className="pm-card p-6 text-sm text-rose-700">
                Could not load admissions: {listQ.error instanceof ApiError ? listQ.error.message : 'unknown error'}
            </div>
        );
    }

    if (admissions.length === 0) {
        return (
            <div className="pm-card p-8 text-center text-slate-500 text-sm">
                No one in observation right now.
            </div>
        );
    }

    return (
        <ul className="space-y-3">
            {admissions.map((a) => (
                <li key={a.id}>
                    <AdmissionCard admission={a} role={role} />
                </li>
            ))}
        </ul>
    );
}

function AdmissionCard({
    admission,
    role,
}: {
    admission: AdmissionView;
    role: ClinicMemberRole | null;
}) {
    const [vitalsOpen, setVitalsOpen] = useState(false);
    const [dischargeOpen, setDischargeOpen] = useState(false);
    const [chartOpen, setChartOpen] = useState(false);
    const [marOpen, setMarOpen] = useState(false);

    const admittedMinsAgo = Math.max(
        0,
        Math.floor((Date.now() - new Date(admission.admitted_at).getTime()) / 60_000),
    );
    const overdue = admission.next_vitals_due_at
        ? new Date(admission.next_vitals_due_at).getTime() <= Date.now()
        : false;
    const dueInMin = admission.next_vitals_due_at
        ? Math.round(
            (new Date(admission.next_vitals_due_at).getTime() - Date.now()) / 60_000,
        )
        : null;

    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link
                            to={`/patients/${admission.patient_id}`}
                            className="text-slate-900 text-sm font-semibold hover:underline"
                        >
                            {admission.patient_full_name ?? 'Unknown patient'}
                        </Link>
                        {admission.hospital_number && (
                            <span className="text-slate-500 text-xs">#{admission.hospital_number}</span>
                        )}
                        {admission.location_label && (
                            <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                                {admission.location_label}
                            </span>
                        )}
                    </div>
                    <div className="text-slate-500 text-xs mt-1 flex flex-wrap gap-x-3">
                        <span>Admitted {admittedMinsAgo} min ago</span>
                        {admission.admitted_by_full_name && (
                            <span>by {admission.admitted_by_full_name}</span>
                        )}
                        {admission.encounter_id && (
                            <Link
                                to={`/encounters/${admission.encounter_id}`}
                                className="text-brand-700 hover:underline"
                            >
                                Open encounter
                            </Link>
                        )}
                    </div>
                    {admission.reason && (
                        <div className="text-slate-700 text-sm mt-2 whitespace-pre-wrap">
                            {admission.reason}
                        </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                        <VitalsChips a={admission} />
                        {admission.vitals_interval_minutes != null && (
                            <span
                                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${overdue ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}
                                title={admission.next_vitals_due_at ?? ''}
                            >
                                {overdue
                                    ? `Vitals overdue by ${Math.abs(dueInMin ?? 0)} min`
                                    : `Vitals in ${dueInMin ?? '?'} min`}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                    <button
                        type="button"
                        className="pm-btn pm-btn-sm"
                        onClick={() => setVitalsOpen(true)}
                    >
                        Record vitals
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost pm-btn-sm"
                        onClick={() => setChartOpen(true)}
                    >
                        Chart
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost pm-btn-sm"
                        onClick={() => setMarOpen(true)}
                    >
                        MAR
                    </button>
                    {canDischarge(role) && (
                        <button
                            type="button"
                            className="pm-btn-primary pm-btn-sm"
                            onClick={() => setDischargeOpen(true)}
                        >
                            Discharge
                        </button>
                    )}
                    {canCancel(role) && (
                        <CancelAdmissionButton admissionId={admission.id} encounterId={admission.encounter_id} />
                    )}
                </div>
            </div>

            {vitalsOpen && (
                <VitalsQuickEntryModal
                    encounterId={admission.encounter_id}
                    admissionId={admission.id}
                    onClose={() => setVitalsOpen(false)}
                    onRecorded={() => setVitalsOpen(false)}
                />
            )}
            {dischargeOpen && (
                <DischargeDialog
                    admissionId={admission.id}
                    encounterId={admission.encounter_id}
                    onClose={() => setDischargeOpen(false)}
                    onDischarged={() => setDischargeOpen(false)}
                />
            )}
            {chartOpen && (
                <ObservationChartModal
                    admissionId={admission.id}
                    onClose={() => setChartOpen(false)}
                />
            )}
            {marOpen && (
                <MarPanel
                    admissionId={admission.id}
                    onClose={() => setMarOpen(false)}
                />
            )}
        </div>
    );
}

function CancelAdmissionButton({
    admissionId,
    encounterId,
}: {
    admissionId: string;
    encounterId: string | null;
}) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const mut = useMutation({
        mutationFn: () =>
            admissionsApi.cancel(admissionId, {
                reason: reason.trim() === '' ? null : reason.trim(),
            }),
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admissions'] }),
                encounterId
                    ? qc.invalidateQueries({ queryKey: ['encounter', encounterId] })
                    : Promise.resolve(),
            ]);
            setOpen(false);
            setReason('');
        },
    });
    if (!open) {
        return (
            <button
                type="button"
                className="pm-btn-ghost pm-btn-sm text-rose-700"
                onClick={() => setOpen(true)}
            >
                Cancel
            </button>
        );
    }
    return (
        <div className="flex flex-col items-end gap-1 min-w-56">
            <input
                className="pm-input"
                type="text"
                placeholder="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={mut.isPending}
                maxLength={500}
            />
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    className="pm-btn-ghost pm-btn-sm"
                    onClick={() => { setOpen(false); setReason(''); }}
                    disabled={mut.isPending}
                >
                    Back
                </button>
                <button
                    type="button"
                    className="pm-btn pm-btn-sm text-rose-700"
                    onClick={() => mut.mutate()}
                    disabled={mut.isPending}
                >
                    {mut.isPending ? 'Cancelling…' : 'Confirm'}
                </button>
            </div>
            {mut.isError && (
                <div className="pm-error text-xs">
                    {mut.error instanceof ApiError ? mut.error.message : 'Cancel failed.'}
                </div>
            )}
        </div>
    );
}

function VitalsChips({ a }: { a: AdmissionView }) {
    const chips: Array<{ k: string; v: string }> = [];
    if (a.latest_bp_systolic != null && a.latest_bp_diastolic != null) {
        chips.push({ k: 'BP', v: `${a.latest_bp_systolic}/${a.latest_bp_diastolic}` });
    }
    if (a.latest_temp_c != null) {
        chips.push({ k: 'T', v: `${a.latest_temp_c.toFixed(1)}°C` });
    }
    if (a.latest_spo2 != null) {
        chips.push({ k: 'SpO₂', v: `${a.latest_spo2}%` });
    }
    if (chips.length === 0) {
        return (
            <span className="inline-block rounded-full bg-slate-50 px-2 py-0.5 text-xs text-slate-500 italic">
                No vitals yet
            </span>
        );
    }
    return (
        <>
            {chips.map((c) => (
                <span
                    key={c.k}
                    className="inline-block rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-800"
                    title={a.latest_vitals_observed_at ? new Date(a.latest_vitals_observed_at).toLocaleString() : undefined}
                >
                    {c.k} {c.v}
                </span>
            ))}
        </>
    );
}
